import { formatDate, formatNumber, formatRelativeDate, getLocaleSnapshot, translate, type Params } from '@/lib/i18n/runtime';
import { useCallback } from 'react';
import { useThemeStore } from '@/store/theme-store';
import { useTranslation } from '@/lib/i18n/react';

/** Render product copy in the current personal locale. Never pass history as keys. */
export const richChatText = (key: string, params: Params = {}) => translate(key, params, 'teamchat');
export const richChatCount = (count: number) => formatNumber(count);
const nameLists = new Map<string, Intl.ListFormat>();
export function richChatNames(values: (string | null | undefined)[]): string | undefined {
  const names = values.filter((value): value is string => typeof value === 'string' && value.length > 0);
  if (!names.length) return undefined;
  const { locale } = getLocaleSnapshot();
  let list = nameLists.get(locale);
  if (!list) { list = new Intl.ListFormat(locale, { type: 'conjunction' }); nameLists.set(locale, list); }
  return list.format(names);
}

export function richChatRelative(iso?: string, now = Date.now()): string {
  return iso && Number.isFinite(Date.parse(iso)) ? formatRelativeDate(iso, undefined, now) : '';
}

export function richChatDate(iso?: string): string {
  return iso && Number.isFinite(Date.parse(iso))
    ? formatDate(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: useThemeStore.getState().timeFormat === '12h' ? 'h12' : 'h23' })
    : '';
}

export function useRichChatClock() {
  const timeFormat = useThemeStore((state) => state.timeFormat);
  const { locale } = useTranslation('teamchat');
  return useCallback((input: string | number | Date | null | undefined): string => {
    if (input === null || input === undefined || input === '') return '';
    const date = input instanceof Date ? input : new Date(input);
    if (!Number.isFinite(date.getTime())) return '';
    return formatDate(date, { hour: '2-digit', minute: '2-digit', hourCycle: timeFormat === '12h' ? 'h12' : 'h23' }, locale);
  }, [locale, timeFormat]);
}

/** Compare civil days, including DST boundaries; do not infer yesterday from elapsed hours. */
export function richChatDay(date: Date, now = new Date()): string {
  if (!Number.isFinite(date.getTime()) || !Number.isFinite(now.getTime())) return '';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return richChatText('view.today');
  if (date.toDateString() === yesterday.toDateString()) return richChatText('view.yesterday');
  return formatDate(date, { day: '2-digit', month: 'short', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

export function richChatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${formatNumber(bytes)} B`;
  const power = bytes < 1024 ** 2 ? 1 : bytes < 1024 ** 3 ? 2 : 3;
  const digits = power === 3 ? 2 : 1;
  return `${formatNumber(bytes / 1024 ** power, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${['', 'KB', 'MB', 'GB'][power]}`;
}
